import { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { getContent } from "../lib/api";
import { Gamepad2, Lightbulb, RefreshCw } from "lucide-react";

function shuffle(a) { return [...a].sort(() => Math.random() - 0.5); }

export default function Game() {
  const { t } = useApp();
  const [cards, setCards] = useState([]);
  const [order, setOrder] = useState([]);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [hintUsed, setHintUsed] = useState(false);

  useEffect(() => {
    getContent("game").then(d => {
      setCards(d);
      setOrder(shuffle(d.map((_, i) => i)));
    });
  }, []);

  if (!cards.length) return <div className="p-6">…</div>;

  const done = idx >= order.length;
  const card = !done && cards[order[idx]];
  const options = !done ? buildOptions(cards, card) : [];

  function buildOptions(all, target) {
    const wrong = shuffle(all.filter(c => c.id !== target.id)).slice(0, 3).map(c => c.meaning_fr);
    return shuffle([target.meaning_fr, ...wrong]);
  }

  const pick = (opt) => {
    if (picked != null) return;
    const correct = opt === card.meaning_fr;
    setPicked(opt);
    if (correct) {
      const newStreak = streak + 1;
      setStreak(newStreak);
      setScore(s => s + 10 + (newStreak >= 3 ? 5 : 0));
    } else {
      setStreak(0);
    }
  };

  const next = () => { setPicked(null); setHintUsed(false); setIdx(i => i + 1); };

  const hint = () => {
    if (hintUsed) return;
    setHintUsed(true);
    setScore(s => Math.max(0, s - 5));
  };

  const restart = () => {
    setOrder(shuffle(cards.map((_, i) => i)));
    setIdx(0); setPicked(null); setScore(0); setStreak(0); setHintUsed(false);
  };

  return (
    <div className="space-y-6 fade-in" data-testid="game-page">
      <header className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-nour-primary text-white flex items-center justify-center"><Gamepad2 className="w-6 h-6" /></div>
        <div>
          <h1 className="font-heading text-2xl font-bold">{t.game}</h1>
          <p className="text-sm text-nour-text2">{t.score}: {score} · {t.streak}: {streak}</p>
        </div>
      </header>

      {!done && card && (
        <article className="nour-card p-8" data-testid="game-card">
          <div className="text-xs text-nour-text2 uppercase tracking-wider text-center">Carte {idx + 1} / {order.length}</div>
          <div className="font-arabic-quran text-6xl text-center text-nour-primary my-6" dir="rtl">{card.word}</div>
          <div className="text-center text-sm text-nour-text2 italic mb-2">{card.translit}</div>

          {hintUsed && <div className="text-center text-xs text-amber-600 mb-3">{`Commence par "${card.meaning_fr[0]}…"`}</div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            {options.map((opt, i) => {
              const correct = picked != null && opt === card.meaning_fr;
              const wrong = picked === opt && opt !== card.meaning_fr;
              return (
                <button
                  key={i}
                  onClick={() => pick(opt)}
                  data-testid={`game-option-${i}`}
                  className={`p-4 rounded-xl border text-sm font-medium transition ${
                    correct ? "bg-emerald-50 border-emerald-400" :
                    wrong   ? "bg-red-50 border-red-400" :
                    picked != null ? "opacity-60" :
                    "bg-white border-[rgba(29,158,117,0.2)] hover:bg-emerald-50"
                  }`}
                >
                  {opt}
                </button>
              );
            })}
          </div>

          <div className="mt-5 flex gap-2 justify-center">
            <button className="btn-ghost" onClick={hint} disabled={hintUsed || picked != null} data-testid="game-hint">
              <Lightbulb className="w-4 h-4"/> {t.hint} (-5)
            </button>
            {picked != null && <button className="btn-primary" onClick={next} data-testid="game-next">{t.next}</button>}
          </div>
        </article>
      )}

      {done && (
        <div className="nour-card p-8 text-center" data-testid="game-result">
          <div className="font-heading text-2xl font-bold">{t.final_score}</div>
          <div className="text-6xl font-extrabold text-nour-primary mt-2">{score}</div>
          <p className="text-sm text-nour-text2 mt-3">{score >= 80 ? t.great_job : t.keep_going}</p>
          <button className="btn-primary mt-5" onClick={restart} data-testid="game-replay"><RefreshCw className="w-4 h-4" /> {t.play_again}</button>
        </div>
      )}
    </div>
  );
}
